import React, { useRef, useCallback, useState } from 'react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * Parse **bold** markers in a string.
 * Returns an array of strings and <strong> elements.
 */
const parseBold = (text) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-bold text-white">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

/**
 * BulletinBoardEditor - Editor for the bulletin-board block.
 * Heading + bold toolbar + textarea with live preview toggle.
 */
const BulletinBoardEditor = ({ block, onChange, theme, hideStylePanels }) => {
  const { content } = block;
  const textareaRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const toggleBold = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    if (start === end) return; // nothing selected

    const text = content.body || '';
    const selected = text.slice(start, end);

    let newText;
    let newStart;
    let newEnd;

    // If already wrapped in **, unwrap
    if (selected.startsWith('**') && selected.endsWith('**') && selected.length > 4) {
      const unwrapped = selected.slice(2, -2);
      newText = text.slice(0, start) + unwrapped + text.slice(end);
      newStart = start;
      newEnd = start + unwrapped.length;
    } else if (
      start >= 2 &&
      text.slice(start - 2, start) === '**' &&
      text.slice(end, end + 2) === '**'
    ) {
      // Selection is inside existing ** markers — remove them
      newText = text.slice(0, start - 2) + selected + text.slice(end + 2);
      newStart = start - 2;
      newEnd = newStart + selected.length;
    } else {
      // Wrap in **
      newText = text.slice(0, start) + '**' + selected + '**' + text.slice(end);
      newStart = start;
      newEnd = end + 4;
    }

    handleChange('body', newText);

    // Restore selection after React re-render
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newStart, newEnd);
    });
  }, [content.body]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      toggleBold();
    }
  }, [toggleBold]);

  const features = BLOCK_FEATURES['bulletin-board'];
  const lines = (content.body || '').split('\n');

  return (
    <div className="space-y-4">
      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Heading (optional)
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="e.g. Club Announcement"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Bold toolbar + textarea */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Bulletin Content
        </label>

        {/* Mini toolbar */}
        <div className="flex items-center gap-2 mb-1">
          <button
            type="button"
            onClick={toggleBold}
            className="px-2 py-1 text-sm font-bold bg-[#1a1a2e] border border-gray-700 rounded hover:border-[#749555] hover:text-white text-gray-400 transition-colors"
            title="Bold selected text (Ctrl+B)"
          >
            B
          </button>
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className={`ml-auto px-2 py-1 text-xs rounded border transition-colors ${
              showPreview
                ? 'bg-[#749555]/20 border-[#749555]/50 text-[#749555]'
                : 'bg-[#1a1a2e] border-gray-700 text-gray-400 hover:border-gray-600'
            }`}
          >
            Preview
          </button>
        </div>

        {showPreview ? (
          /* Live preview */
          <div
            className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg min-h-[240px] cursor-text"
            onClick={() => setShowPreview(false)}
          >
            {content.heading && (
              <p className="text-white font-bold text-base mb-2">{content.heading}</p>
            )}
            <div className="space-y-0.5">
              {lines.map((line, i) =>
                line.trim() === '' ? (
                  <div key={i} className="h-2" />
                ) : (
                  <p key={i} className="text-sm text-gray-300 leading-relaxed">
                    {parseBold(line)}
                  </p>
                )
              )}
            </div>
          </div>
        ) : (
          /* Textarea */
          <textarea
            ref={textareaRef}
            value={content.body || ''}
            onChange={(e) => handleChange('body', e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter bulletin details..."
            rows={10}
            className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none font-mono text-sm leading-relaxed"
          />
        )}
        <p className="text-xs text-gray-500 mt-1">
          Wrap text in <span className="font-mono">**double asterisks**</span> for bold
        </p>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
        <SizeSpacingPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={features}
        />
        <ColorsFontsPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={features}
          theme={theme || {}}
          blockType="bulletin-board"
        />
      </>)}
    </div>
  );
};

export default BulletinBoardEditor;
